import { PO_REVIEW_URL } from "../MainAppPanel";
import Rfq from "../models/Rfq";
import PurchaseOrder from "../models/PurchaseOrder";
import { POStatus } from "../models/POStatus";
import { RFQStatus } from "../models/RFQStatus";
import { RfqHomework } from "../models/RfqHomework";
import { RfqItem } from "../models/RfqItem";

const VENDOR_ORDER_PO_STATUSES = new Set<POStatus>([
  POStatus.APPROVED,
  POStatus.SUBMITTED,
  POStatus.IN_PROGRESS,
  POStatus.COMPLETED,
  POStatus.CLOSED,
]);

function poMatchesRfq(po: PurchaseOrder, rfq: Rfq): boolean {
  if (po.rfqId != null && po.rfqId === rfq.id) return true;
  if (po.quoteId == null) return false;
  return (rfq.quotes ?? []).some((q) => q.id === po.quoteId);
}

/** Map homework id → RFQ id for bulk PO matching (avoids per-PO Set allocs). */
function homeworkIdToRfqId(rfqs: Rfq[]): Map<number, number> {
  const map = new Map<number, number>();
  for (const rfq of rfqs) {
    for (const q of rfq.quotes ?? []) {
      map.set(q.id, rfq.id);
    }
  }
  return map;
}

function resolvePoRfqId(
  po: PurchaseOrder,
  homeworkToRfq: Map<number, number>
): number | null {
  if (po.rfqId != null && po.rfqId > 0) return po.rfqId;
  if (po.quoteId != null) return homeworkToRfq.get(po.quoteId) ?? null;
  return null;
}

/** RFQ statuses that belong on Vendor Orders / Completed POs (not Work Queue). */
export function isVendorOrderPhaseStatus(status: RFQStatus | string): boolean {
  return status === RFQStatus.ORDER || status === RFQStatus.COMPLETED;
}

/**
 * RFQ ids that have an approved+ vendor-order PO, or Order/Completed status —
 * O(POs + homework + RFQs) once, instead of O(RFQs × POs) via isVendorOrderRfq.
 */
export function vendorOrderRfqIdSet(
  rfqs: Rfq[],
  purchaseOrders: PurchaseOrder[]
): Set<number> {
  const homeworkToRfq = homeworkIdToRfqId(rfqs);
  const ids = new Set<number>();
  for (const po of purchaseOrders) {
    if (!VENDOR_ORDER_PO_STATUSES.has(po.status)) continue;
    const rfqId = resolvePoRfqId(po, homeworkToRfq);
    if (rfqId != null) ids.add(rfqId);
  }
  for (const rfq of rfqs) {
    if (isVendorOrderPhaseStatus(rfq.status)) ids.add(rfq.id);
  }
  return ids;
}

/**
 * Newest inbound DRAFT PO (emailId set) per RFQ — one pass over the PO list.
 */
export function latestDraftInboundPoByRfqId(
  rfqs: Rfq[],
  purchaseOrders: PurchaseOrder[]
): Map<number, PurchaseOrder> {
  const homeworkToRfq = homeworkIdToRfqId(rfqs);
  const byRfq = new Map<number, PurchaseOrder>();
  for (const po of purchaseOrders) {
    if (po.status !== POStatus.DRAFT || po.emailId == null) continue;
    const rfqId = resolvePoRfqId(po, homeworkToRfq);
    if (rfqId == null) continue;
    const prev = byRfq.get(rfqId);
    if (
      !prev ||
      new Date(po.createdAt).getTime() > new Date(prev.createdAt).getTime()
    ) {
      byRfq.set(rfqId, po);
    }
  }
  return byRfq;
}

/** PO numbers per RFQ — RFQ's own poNumber plus every linked PO's poNum. */
export function poNumbersByRfqId(
  rfqs: Rfq[],
  purchaseOrders: PurchaseOrder[]
): Map<number, string[]> {
  const homeworkToRfq = homeworkIdToRfqId(rfqs);
  const byRfq = new Map<number, string[]>();
  const add = (rfqId: number, value: string | null | undefined) => {
    const poNum = (value ?? "").trim();
    if (!poNum) return;
    const list = byRfq.get(rfqId);
    if (!list) {
      byRfq.set(rfqId, [poNum]);
    } else if (!list.includes(poNum)) {
      list.push(poNum);
    }
  };
  for (const rfq of rfqs) {
    add(rfq.id, rfq.poNumber);
  }
  for (const po of purchaseOrders) {
    const rfqIds = new Set<number>(po.linkedRfqIds ?? []);
    const primary = resolvePoRfqId(po, homeworkToRfq);
    if (primary != null) rfqIds.add(primary);
    for (const id of po.additionalQuoteIds ?? []) {
      const rfqId = homeworkToRfq.get(id);
      if (rfqId != null) rfqIds.add(rfqId);
    }
    rfqIds.forEach((rfqId) => add(rfqId, po.poNum));
  }
  return byRfq;
}

/** Same default homework selection as RfqPanel. */
function primaryHomework(rfq: Rfq): RfqHomework | undefined {
  const quotes = rfq.quotes ?? [];
  const submitted = quotes.filter((q) => q.submittedById !== null);
  if (submitted.length > 0) {
    return [...submitted].sort((a, b) => {
      const dateA = a.submissionDate
        ? new Date(a.submissionDate).getTime()
        : 0;
      const dateB = b.submissionDate
        ? new Date(b.submissionDate).getTime()
        : 0;
      return dateB - dateA;
    })[0];
  }
  const unsubmitted = quotes.filter((q) => q.submittedById === null);
  return unsubmitted[0] ?? quotes[0];
}

function selectedVendorQuote(item: RfqItem) {
  if (item.selectedVendorQuoteId == null) return undefined;
  return item.vendorQuotes?.find((q) => q.id === item.selectedVendorQuoteId);
}

/**
 * Every line is either marked notBoughtYet, or has a selected vendor quote with
 * a non-empty order confirmation number and orderConfirmationReceived true.
 */
export function isRfqFullyOrderConfirmed(rfq: Rfq): boolean {
  if (typeof rfq.listFullyOrderConfirmed === "boolean") {
    return rfq.listFullyOrderConfirmed;
  }
  const homework = primaryHomework(rfq);
  const items = homework?.items ?? [];
  if (items.length === 0) return false;
  return items.every((item) => {
    if (item.notBoughtYet) return true;
    const quote = selectedVendorQuote(item);
    if (!quote) return false;
    const confirmation = (quote.orderConfirmation ?? "").trim();
    return confirmation.length > 0 && Boolean(quote.orderConfirmationReceived);
  });
}

/** Newest DRAFT inbound PO (has emailId) for an RFQ. */
export function latestDraftInboundPurchaseOrder(
  rfq: Rfq,
  purchaseOrders: PurchaseOrder[]
): PurchaseOrder | null {
  const matches = purchaseOrders.filter((po) => {
    if (po.status !== POStatus.DRAFT) return false;
    if (po.emailId == null) return false;
    return poMatchesRfq(po, rfq);
  });
  matches.sort(
    (a, b) =>
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
  return matches[0] ?? null;
}

/**
 * Prefer a draft inbound PO for rerun/review; otherwise the newest PO linked
 * to this RFQ (including approved / vendor-order statuses).
 */
export function latestLinkedPurchaseOrder(
  rfq: Rfq,
  purchaseOrders: PurchaseOrder[]
): PurchaseOrder | null {
  const draft = latestDraftInboundPurchaseOrder(rfq, purchaseOrders);
  if (draft) return draft;
  const matches = purchaseOrders.filter((po) => poMatchesRfq(po, rfq));
  matches.sort(
    (a, b) =>
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
  const withEmail = matches.find((po) => po.emailId != null);
  return withEmail ?? matches[0] ?? null;
}

export function workQueuePurchaseOrderStatus(
  rfq: Rfq,
  purchaseOrders: PurchaseOrder[]
): "" | "Received" {
  return latestDraftInboundPurchaseOrder(rfq, purchaseOrders)
    ? "Received"
    : "";
}

/** Inbound DRAFT PO recognized from email but not yet linked to an RFQ. */
export function unmatchedInboundPoByEmailId(
  emailId: number,
  inboundDraftPurchaseOrders: PurchaseOrder[]
): PurchaseOrder | undefined {
  return inboundDraftPurchaseOrders.find(
    (po) =>
      po.emailId === emailId &&
      po.status === POStatus.DRAFT &&
      po.quoteId == null
  );
}

/** Newest approved (or later) PO for an RFQ — lives in Vendor Orders. */
export function latestVendorOrderPurchaseOrder(
  rfq: Rfq,
  purchaseOrders: PurchaseOrder[]
): PurchaseOrder | null {
  const matches = purchaseOrders.filter(
    (po) =>
      VENDOR_ORDER_PO_STATUSES.has(po.status) && poMatchesRfq(po, rfq)
  );
  matches.sort(
    (a, b) =>
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
  return matches[0] ?? null;
}

/**
 * RFQ has left Work Queue — Vendor Orders or Completed POs.
 * Driven by Order/Completed status and/or an approved+ PO.
 */
export function isVendorOrderRfq(
  rfq: Rfq,
  purchaseOrders: PurchaseOrder[]
): boolean {
  if (isVendorOrderPhaseStatus(rfq.status)) return true;
  return latestVendorOrderPurchaseOrder(rfq, purchaseOrders) != null;
}

/**
 * Every buyable line's selected vendor quote has been order-emailed, or already
 * has OC progress (so in-flight RFQs stay on Order Conf Needed).
 */
export function isRfqAllOrdersEmailed(rfq: Rfq): boolean {
  if (typeof rfq.listAllOrdersEmailed === "boolean") {
    return rfq.listAllOrdersEmailed;
  }
  const homework = primaryHomework(rfq);
  const items = homework?.items ?? [];
  if (items.length === 0) return false;
  return items.every((item) => {
    if (item.notBoughtYet) return true;
    const quote = selectedVendorQuote(item);
    if (!quote) return false;
    if (Boolean(quote.orderEmailed)) return true;
    const confirmation = (quote.orderConfirmation ?? "").trim();
    return confirmation.length > 0 || Boolean(quote.orderConfirmationReceived);
  });
}

/**
 * Still in Vendor Orders: Order / order-confirmation phase (not Completed).
 * Status Completed always wins; otherwise approved Order + incomplete OCs.
 */
export function isActiveVendorOrderRfq(
  rfq: Rfq,
  purchaseOrders: PurchaseOrder[]
): boolean {
  if (rfq.status === RFQStatus.COMPLETED) return false;
  if (rfq.status === RFQStatus.ORDER) {
    return !isRfqFullyOrderConfirmed(rfq);
  }
  return (
    isVendorOrderRfq(rfq, purchaseOrders) && !isRfqFullyOrderConfirmed(rfq)
  );
}

/** Orders tab: vendor-order phase, order not yet emailed for every buyable line. */
export function isOrdersQueueRfq(
  rfq: Rfq,
  purchaseOrders: PurchaseOrder[]
): boolean {
  return (
    isActiveVendorOrderRfq(rfq, purchaseOrders) && !isRfqAllOrdersEmailed(rfq)
  );
}

/** Order Conf Needed: vendor-order phase, orders emailed, OC incomplete. */
export function isOrderConfNeededRfq(
  rfq: Rfq,
  purchaseOrders: PurchaseOrder[]
): boolean {
  return (
    isActiveVendorOrderRfq(rfq, purchaseOrders) && isRfqAllOrdersEmailed(rfq)
  );
}

/**
 * Completed POs: status Completed, or vendor-order phase with every OC received.
 */
export function isCompletedPoRfq(
  rfq: Rfq,
  purchaseOrders: PurchaseOrder[]
): boolean {
  if (rfq.status === RFQStatus.COMPLETED) return true;
  if (!isVendorOrderRfq(rfq, purchaseOrders)) return false;
  return isRfqFullyOrderConfirmed(rfq);
}

/** RFQs still in Orders (approved PO, order not fully emailed). */
export function countOrdersQueueItems(
  rfqs: Rfq[],
  purchaseOrders: PurchaseOrder[]
): number {
  let count = 0;
  for (const rfq of rfqs) {
    if (isOrdersQueueRfq(rfq, purchaseOrders)) count += 1;
  }
  return count;
}

/** RFQs still in Order Conf Needed (emailed, OC incomplete). */
export function countOrderConfNeededQueueItems(
  rfqs: Rfq[],
  purchaseOrders: PurchaseOrder[]
): number {
  let count = 0;
  for (const rfq of rfqs) {
    if (isOrderConfNeededRfq(rfq, purchaseOrders)) count += 1;
  }
  return count;
}

/** RFQs still in Vendor Orders (approved PO, not yet fully confirmed). */
export function countVendorOrderQueueItems(
  rfqs: Rfq[],
  purchaseOrders: PurchaseOrder[]
): number {
  return (
    countOrdersQueueItems(rfqs, purchaseOrders) +
    countOrderConfNeededQueueItems(rfqs, purchaseOrders)
  );
}

/** RFQs that belong in Completed POs (approved PO, fully confirmed). */
export function countCompletedPoQueueItems(
  rfqs: Rfq[],
  purchaseOrders: PurchaseOrder[]
): number {
  const vendorOrderIds = vendorOrderRfqIdSet(rfqs, purchaseOrders);
  let count = 0;
  for (const rfq of rfqs) {
    if (rfq.status === RFQStatus.COMPLETED) {
      count += 1;
      continue;
    }
    const inVendorPhase =
      rfq.status === RFQStatus.ORDER || vendorOrderIds.has(rfq.id);
    if (inVendorPhase && isRfqFullyOrderConfirmed(rfq)) {
      count += 1;
    }
  }
  return count;
}

/** Open/in-progress RFQs still on Work Queue (excludes Vendor Orders). */
export function countWorkQueueRfqItems(
  rfqs: Rfq[],
  purchaseOrders: PurchaseOrder[]
): number {
  const vendorOrderIds = vendorOrderRfqIdSet(rfqs, purchaseOrders);
  const draftByRfq = latestDraftInboundPoByRfqId(rfqs, purchaseOrders);
  let count = 0;
  for (const rfq of rfqs) {
    const isSamGovDraft =
      rfq.status === RFQStatus.NEW && Boolean(rfq.samGovNoticeId?.trim());
    const isActive =
      rfq.status === RFQStatus.OPEN ||
      rfq.status === RFQStatus.IN_PROGRESS ||
      isSamGovDraft;
    const isArchivedWithPendingPo =
      rfq.status !== RFQStatus.NEW &&
      rfq.status !== RFQStatus.OPEN &&
      rfq.status !== RFQStatus.IN_PROGRESS &&
      draftByRfq.has(rfq.id);
    if (
      (isActive || isArchivedWithPendingPo) &&
      !vendorOrderIds.has(rfq.id)
    ) {
      count += 1;
    }
  }
  return count;
}

/** RFQs with a pending inbound PO (green Work Queue rows), including archived. */
export function countPendingInboundPoQueueItems(
  rfqs: Rfq[],
  purchaseOrders: PurchaseOrder[]
): number {
  const vendorOrderIds = vendorOrderRfqIdSet(rfqs, purchaseOrders);
  const draftByRfq = latestDraftInboundPoByRfqId(rfqs, purchaseOrders);
  let count = 0;
  for (const rfq of rfqs) {
    if (vendorOrderIds.has(rfq.id)) continue;
    if (!draftByRfq.has(rfq.id)) continue;
    const isActive =
      rfq.status === RFQStatus.OPEN || rfq.status === RFQStatus.IN_PROGRESS;
    const isArchived =
      rfq.status !== RFQStatus.NEW &&
      rfq.status !== RFQStatus.OPEN &&
      rfq.status !== RFQStatus.IN_PROGRESS;
    if (isActive || isArchived) count += 1;
  }
  return count;
}

const LAST_WORK_QUEUE_RFQ_KEY = "procurement.lastWorkQueueRfqId";

/** Remember the RFQ the user is currently in so Work Queue can highlight it. */
export function rememberLastWorkQueueRfq(rfqId: number): void {
  if (!Number.isFinite(rfqId) || rfqId <= 0) return;
  try {
    sessionStorage.setItem(LAST_WORK_QUEUE_RFQ_KEY, String(rfqId));
  } catch {
    /* private mode / quota */
  }
}

export function getLastWorkQueueRfqId(): number | null {
  try {
    const id = Number(sessionStorage.getItem(LAST_WORK_QUEUE_RFQ_KEY));
    return Number.isFinite(id) && id > 0 ? id : null;
  } catch {
    return null;
  }
}

/** Open the full-screen PO comparison route in a new browser tab. */
export function openPurchaseOrderReviewInNewTab(poId: number): Window | null {
  const path = PO_REVIEW_URL.replace(":poId", String(poId));
  const url = `${window.location.origin}${path}`;
  // Keep window.opener so dismiss/approve can focus the original tab and close.
  return window.open(url, "_blank");
}
